/*
 * Request validation, shared by the server and the admin forms.
 *
 * Nearly every constraint here has a counterpart in the database: column
 * lengths, the slug character class, "title must not be blank". The database is
 * still the authority - a race between two admins is caught by the unique index,
 * not by this file - but validating the same rules in one place means the admin
 * UI can explain a problem in a field label instead of showing the user a SQL
 * error, and the server never has to trust the client to have done it.
 *
 * The exception is RESERVED_PRODUCT_SLUGS, which has no counterpart and cannot
 * have one: it is a rule about the application's ROUTES, and the database has
 * never been told what a URL looks like. See the comment on it.
 *
 * Lengths are taken from the DDL:
 *   Title       nvarchar(300) on kb.ArticleRevision; nvarchar(200) and one line
 *               on kb.ReleaseNoteRevision (CK_ReleaseNoteRevision_Title)
 *   Version     varchar(40) on kb.ReleaseNote, the grammar of CK_ReleaseNote_Version
 *   ChangeNote  nvarchar(500) on both revision tables
 *   Content     nvarchar(max) on both revision tables, capped here at 4,000,000
 *   Slug        nvarchar(300) on kb.Article, nvarchar(200) on the catalogues
 *   Name        nvarchar(200)
 *   FileName    nvarchar(200) on kb.ArticleImage and kb.ReleaseNoteImage
 *
 * Release notes (S42, 2026-10-12) get schemas of their own below rather than
 * reusing the article ones, because the two disagree on the title - 200 and one
 * line against 300 - and on every message that names the thing being edited.
 * They live in this file because the building blocks they share with the article
 * schemas (change note, product, search, page) are private to it.
 */
import { z } from 'zod';
import { ARTICLE_IMAGE_FILE_NAME_MAX_LENGTH, ARTICLE_IMAGE_FILE_NAME_PATTERN, ARTICLE_IMAGE_MAX_BYTES, ARTICLE_IMAGE_MAX_PER_ARTICLE, describeArticleImageFileNameRequirement, } from './articleImages.js';
import { attachmentFileNameProblem, normaliseAttachmentFileName } from './attachmentFiles.js';
import { RELEASE_NOTE_IMAGE_MAX_PER_NOTE, RELEASE_NOTE_TITLE_MAX_LENGTH, describeReleaseNoteVersionRequirement, isSingleLineTitle, isValidReleaseNoteVersion, } from './releaseNotes.js';
import { ARTICLE_VISIBILITIES, AUDIENCE_KEY_PATTERN, MAX_ARTICLE_AUDIENCES } from './articleVisibility.js';
import { ARTICLE_SLUG_MAX_LENGTH, SLUG_MAX_LENGTH, SLUG_PATTERN } from './slug.js';
import { SITE_SUBTITLE_MAX_LENGTH, SITE_TITLE_MAX_LENGTH } from './siteSettings.js';
import { SITE_THEMES } from './siteThemes.js';
const SLUG_MESSAGE = 'Use lowercase letters a-z, digits and hyphens only.';
export const slugSchema = z
    .string()
    .trim()
    .min(1, 'A slug is required.')
    .max(SLUG_MAX_LENGTH, `At most ${SLUG_MAX_LENGTH} characters.`)
    .regex(SLUG_PATTERN, SLUG_MESSAGE);
export const articleSlugSchema = z
    .string()
    .trim()
    .min(1, 'A slug is required.')
    .max(ARTICLE_SLUG_MAX_LENGTH, `At most ${ARTICLE_SLUG_MAX_LENGTH} characters.`)
    .regex(SLUG_PATTERN, SLUG_MESSAGE);
/** ISO-8601 instant. Rejects the shapes Date.parse would silently accept. */
export const isoDateTimeSchema = z
    .string()
    .trim()
    .refine((value) => !Number.isNaN(Date.parse(value)), 'Not a valid date and time.')
    .refine((value) => /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d{1,7})?)?(Z|[+-]\d{2}:?\d{2})?$/.test(value), 'Use an ISO-8601 date and time.');
const titleSchema = z
    .string()
    .trim()
    .min(1, 'A title is required.')
    .max(300, 'At most 300 characters.');
const changeNoteSchema = z
    .string()
    .trim()
    .max(500, 'At most 500 characters.')
    .nullish()
    .transform((value) => (value && value.length > 0 ? value : null));
/**
 * The body cap, one number for both kinds of content: generous enough that no
 * real text meets it, small enough that a runaway paste is refused by the schema
 * rather than written to an nvarchar(max) column.
 */
const CONTENT_MAX_LENGTH = 4_000_000;
const contentSchema = z.string().max(CONTENT_MAX_LENGTH, 'The article body is too large.');
/** Base64 rowversion, round-tripped for optimistic concurrency. */
export const rowVersionSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9+/=]+$/, 'Malformed concurrency token.');
const idListSchema = z.array(z.number().int().positive()).max(200);
// ---------------------------------------------------------------------------
// Metadata
// ---------------------------------------------------------------------------
const nameSchema = z
    .string()
    .trim()
    .min(1, 'A name is required.')
    .max(200, 'At most 200 characters.');
/**
 * A category name may not contain a comma. kb.ArticleDetail lists an article's
 * category names as one comma-joined string (CategoryTags), and the server splits
 * that string on the comma to hand the web one tag per name - so "Reports,
 * exports" would reach a reader as two tags. The database refuses the character
 * too (CK_Category_Name, 2026-09-23, ENHANCEMENTS W11); this is the same rule in
 * the field's own voice, so the admin form can say it before the request leaves
 * and the API answers 422 under `name` rather than a constraint error. Product
 * names have no such rule and need none: the product is a column, never
 * aggregated into a string.
 */
export function isValidCategoryName(name) {
    return !name.includes(',');
}
export function describeCategoryNameRequirement() {
    return 'A category name cannot contain a comma.';
}
/**
 * Product slugs that would shadow a route, and are therefore refused.
 *
 * A product slug is the FIRST segment of every reader URL - /kb/<product>/<slug>
 * - and of the public article endpoint. Two of these would be read as something
 * else entirely: `id` collides with the permalink /kb/id/<articleId>, and
 * `by-id` with /api/public/articles/by-id/<articleId>. `kb` merely produces
 * /kb/kb/<slug>, which routes correctly and reads as a bug report waiting to
 * happen. `admin`, `api` and `assets` are the site's other top-level paths: a
 * product named for one of them is a trap set for whoever next adds a route.
 * `releasenotes` joined them on 2026-10-12 (S42) for the same reason, the day it
 * became a top-level path: the release-note pages live at
 * /releasenotes/<product>/<version>, and no route today puts a product slug
 * where it would collide - but the next one might.
 *
 * Enforced here rather than by a CHECK constraint on kb.Product.Slug, and the
 * difference matters: the database could only answer with a constraint
 * violation, which the admin dialog would have to show as a banner. From the
 * schema it is a field error under the Slug input, for the admin form and for
 * any API caller alike. The database still owns the CHARACTER rule and
 * uniqueness; this is a rule about the application's routes, which is not
 * something the database knows about.
 */
export const RESERVED_PRODUCT_SLUGS = [
    'id',
    'by-id',
    'admin',
    'api',
    'assets',
    'kb',
    'releasenotes',
];
export const productInputSchema = z.object({
    name: nameSchema,
    slug: slugSchema.refine((value) => !RESERVED_PRODUCT_SLUGS.includes(value), (value) => ({ message: `"${value}" is reserved for site addresses. Choose another slug.` })),
    isActive: z.boolean().default(true),
});
/**
 * The same three fields, WITHOUT the reserved-word rule and WITH the comma rule
 * on the name. A category slug never appears in a path - categories are filtered
 * by id from the browse query - so a category called "admin" shadows nothing,
 * and refusing it would be a rule with no reason behind it. The name, on the
 * other hand, is aggregated into a comma-joined display string, which the product
 * name never is (isValidCategoryName above). Spelled out rather than aliased to
 * the product schema, as it used to be, so the differences are visible instead of
 * accidental.
 *
 * And, since 2026-11-05 (S52), the products the category relates to:
 * `productIds`, product tag ids - any number, an empty list for none, a
 * repeated id folded into one. OPTIONAL rather than defaulted: a request that
 * leaves it out keeps what is stored (a Metadata page loaded before the deploy
 * saves a rename without wiping the relations), and a create that leaves it out
 * relates to nothing. Whether an id names a product is
 * FK_CategoryProduct_Product's to say; the repository turns that refusal into a
 * field error.
 */
const CATEGORY_PRODUCT_MESSAGE = 'Choose product tags, or none.';
export const categoryInputSchema = z.object({
    name: nameSchema.refine(isValidCategoryName, describeCategoryNameRequirement()),
    slug: slugSchema,
    isActive: z.boolean().default(true),
    productIds: z
        .array(z
        .number({ invalid_type_error: CATEGORY_PRODUCT_MESSAGE })
        .int(CATEGORY_PRODUCT_MESSAGE)
        .positive(CATEGORY_PRODUCT_MESSAGE), { invalid_type_error: CATEGORY_PRODUCT_MESSAGE })
        .transform((ids) => [...new Set(ids)])
        .optional(),
});
// ---------------------------------------------------------------------------
// Articles
// ---------------------------------------------------------------------------
/**
 * One message for every way `productId` can be wrong - missing, null, a string,
 * a fraction, zero. The field is a single-select the form fills in, so there is
 * no typo for a caller to correct and nothing useful to distinguish: each of
 * those means the same thing to whoever is looking at the form, and it is what
 * the category field's message says in the same voice.
 */
const PRODUCT_REQUIRED = 'Assign a product.';
/**
 * The one product an article or a release note carries, by id: every way of
 * getting it wrong says PRODUCT_REQUIRED. One schema for both, so the two editors
 * cannot come to disagree about what a product choice is.
 */
const productIdSchema = z
    .number({ required_error: PRODUCT_REQUIRED, invalid_type_error: PRODUCT_REQUIRED })
    .int(PRODUCT_REQUIRED)
    .positive(PRODUCT_REQUIRED);
/**
 * The properties of the article row itself: what it is tagged with, when it
 * goes live and whether it is visible at all.
 *
 * The slug lives here, on the article, because it is half of the article's
 * address - the other half being the product. kb.Article.Slug is unique only
 * within a product (UQ_Article_ProductId_Slug), so the two fields below are one
 * decision and are sent together: changing either one moves the article to a new
 * URL and rewrites the tokens pointing at the old one.
 *
 * One product, exactly: `productId` rather than a list, mirroring the NOT NULL
 * column and the single-select the editor now shows. Categories stay a list, and
 * "at least one category" stays an authoring rule the database cannot express
 * (see datamodel/README.md) - so it is enforced here, on create and on update,
 * in the one place both go through.
 */
export const articlePropertiesSchema = z.object({
    slug: articleSlugSchema,
    productId: productIdSchema,
    categoryIds: idListSchema.min(1, 'Assign at least one category tag.'),
    publishAtUtc: isoDateTimeSchema,
    isVisible: z.boolean(),
});
/**
 * One audience key, as CK_ArticleAudience_AudienceKey spells it. Whether the
 * key is one this deployment CONFIGURES is not something this package can know
 * - the audiences are the server's application settings - so the server checks
 * that after parsing, and answers on the same field.
 */
export const audienceKeySchema = z
    .string({ invalid_type_error: 'An audience is identified by its key.' })
    .regex(AUDIENCE_KEY_PATTERN, 'An audience key is A-Z, digits and underscores, at most 100 characters.');
const AUDIENCE_REQUIRED = 'Choose at least one audience, or make the article public.';
const audienceKeysSchema = z
    .array(audienceKeySchema)
    .max(MAX_ARTICLE_AUDIENCES, `An article can name at most ${MAX_ARTICLE_AUDIENCES} audiences.`);
/**
 * Who may read the article (2026-10-26, S46), as the two fields the editor
 * sends together: `visibility`, and the audiences a private article is for.
 *
 * A private article names at least one audience - otherwise only the
 * administrators could read it, which is never what an author means - and a
 * public one names none: its keys are dropped here, so the save clears any rows
 * an earlier private spell left behind. Repeated keys collapse to one. An
 * authoring rule, like "at least one category": kb.ArticleAudience cannot
 * require a row of itself.
 */
function refineAudiences(value, context) {
    if (value.visibility === 'private' && value.audienceKeys.length === 0) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['audienceKeys'], message: AUDIENCE_REQUIRED });
    }
}
function normaliseAudiences(value) {
    return {
        ...value,
        audienceKeys: value.visibility === 'private' ? [...new Set(value.audienceKeys)] : [],
    };
}
/**
 * The update REQUIRES visibility, where the create defaults it. A Settings-style
 * reason (S45): a page loaded before this field existed sends a properties save
 * without it, and reading that absence as "public" would silently open a private
 * article to everybody - so it is refused, with a message that says to reload.
 */
export const articleUpdateSchema = articlePropertiesSchema
    .extend({
    rowVersion: rowVersionSchema,
    visibility: z.enum(ARTICLE_VISIBILITIES, {
        required_error: 'Choose who can read the article. If the form has no Visibility field, reload the page.',
        invalid_type_error: 'Visibility is "public" or "private".',
    }),
    audienceKeys: audienceKeysSchema.default([]),
})
    .superRefine(refineAudiences)
    .transform(normaliseAudiences);
/**
 * Creating an article writes its first revision in the same transaction, and
 * that revision is always the current one: an article with no revision has no
 * title, cannot be listed sensibly and cannot go live whatever its gates say.
 * Parking a draft behind published text is a later act, not a way to start.
 *
 * Flat rather than nesting the revision fields, because there is no choice to
 * express here - title and content are not optional - and a nested object would
 * only add a level to every field-error path the form has to index by.
 */
export const articleCreateSchema = articlePropertiesSchema
    .extend({
    title: titleSchema,
    content: contentSchema,
    changeNote: changeNoteSchema,
    // Public unless the author chose otherwise (S46): a brand-new article has
    // no earlier state to protect, so a missing field here is the default.
    visibility: z
        .enum(ARTICLE_VISIBILITIES, { invalid_type_error: 'Visibility is "public" or "private".' })
        .default('public'),
    audienceKeys: audienceKeysSchema.default([]),
})
    .superRefine(refineAudiences)
    .transform(normaliseAudiences);
/**
 * Appending a revision. `publish` promotes it to IsCurrent in the same
 * transaction; leaving it false is how a draft is parked behind the published
 * text, which the filtered unique index makes possible.
 */
export const revisionCreateSchema = z.object({
    title: titleSchema,
    content: contentSchema,
    changeNote: changeNoteSchema,
    publish: z.boolean().default(false),
    /**
     * The revision the editor started from. When it is no longer the latest, a
     * second admin has saved in the meantime and the request is refused.
     */
    baseRevisionNumber: z.number().int().min(0).nullish(),
});
/**
 * The three publishing actions, and nothing else.
 *
 * Each one sets the article gates and nothing more. There is exactly one
 * visibility flag in the schema, so "publish now" has a single, complete
 * meaning: IsVisible = 1 and PublishAtUtc = now. Whether readers then see text
 * depends only on whether a revision is current, which is the editor's business
 * and not a publishing option.
 */
export const publishActionSchema = z.discriminatedUnion('action', [
    z.object({
        action: z.literal('publish-now'),
        rowVersion: rowVersionSchema,
    }),
    z.object({
        action: z.literal('schedule'),
        publishAtUtc: isoDateTimeSchema,
        rowVersion: rowVersionSchema,
    }),
    z.object({
        action: z.literal('hide'),
        rowVersion: rowVersionSchema,
    }),
]);
// ---------------------------------------------------------------------------
// Release notes (S42, 2026-10-12)
// ---------------------------------------------------------------------------
const RELEASE_NOTE_TITLE_SINGLE_LINE = 'A release note title is a single line.';
/**
 * A release note's title: trimmed, not blank, at most 200 characters, and one
 * line - the owner's "single line of text", which CK_ReleaseNoteRevision_Title
 * enforces by refusing a carriage return or a line feed. The trim runs first, so
 * a stray newline at either END is simply removed; only one INSIDE the title is
 * refused, under `title`, in the field's voice.
 *
 * Not the article's titleSchema with a smaller number: that one allows 300 and
 * tolerates newlines, and both differences are the point.
 */
export const releaseNoteTitleSchema = z
    .string()
    .trim()
    .min(1, 'A title is required.')
    .max(RELEASE_NOTE_TITLE_MAX_LENGTH, `At most ${RELEASE_NOTE_TITLE_MAX_LENGTH} characters.`)
    .refine(isSingleLineTitle, RELEASE_NOTE_TITLE_SINGLE_LINE);
const RELEASE_NOTE_VERSION_MESSAGE = describeReleaseNoteVersionRequirement();
/**
 * A release note's version, trimmed and then held to the grammar of
 * CK_ReleaseNote_Version - which includes the 40-character width, since the
 * grammar cannot produce anything longer (releaseNotes.ts).
 *
 * ONE message for every way it can be wrong - missing, not a string, blank, too
 * long, an uppercase V, a leading zero - as PRODUCT_REQUIRED does for the product:
 * the message says what a version looks like, which is the one thing the author
 * needs whatever the slip was, and a too-long version would otherwise collect a
 * length message and a grammar message for one mistake. NOT normalised: a caller
 * that sends `V1.2.3.4` is told so rather than stored at an address it did not
 * name; it is the editor's input, not the contract, that pins the v.
 */
export const releaseNoteVersionSchema = z
    .string({
    required_error: RELEASE_NOTE_VERSION_MESSAGE,
    invalid_type_error: RELEASE_NOTE_VERSION_MESSAGE,
})
    .trim()
    .refine(isValidReleaseNoteVersion, RELEASE_NOTE_VERSION_MESSAGE);
/** The release-note body: the article cap, in a message that does not say "article". */
const releaseNoteContentSchema = z
    .string()
    .max(CONTENT_MAX_LENGTH, 'The release note body is too large.');
/**
 * The properties of the release-note row itself: its address - the product and
 * the version, unique together (UQ_ReleaseNote_ProductId_Version) - and its two
 * publishing gates. Saved under the rowversion and NOT in the change history,
 * exactly as an article's slug and product are not; the title and the body are
 * the revisioned part.
 *
 * No categories: a release note has none.
 */
export const releaseNotePropertiesSchema = z.object({
    productId: productIdSchema,
    version: releaseNoteVersionSchema,
    publishAtUtc: isoDateTimeSchema,
    isVisible: z.boolean(),
});
export const releaseNoteUpdateSchema = releaseNotePropertiesSchema.extend({
    rowVersion: rowVersionSchema,
});
/**
 * Creating a release note writes its first revision in the same transaction, and
 * that revision is current - articleCreateSchema's rule and its reasons. Flat for
 * the same reason too. The body may be empty, as an article's may on create.
 */
export const releaseNoteCreateSchema = releaseNotePropertiesSchema.extend({
    title: releaseNoteTitleSchema,
    content: releaseNoteContentSchema,
    changeNote: changeNoteSchema,
});
/**
 * Appending a release-note revision: revisionCreateSchema with the release-note
 * title. `publish` promotes it in the same transaction; false parks a draft
 * behind the published text. `baseRevisionNumber` is the revision the editor
 * started from, and a save is refused when it is no longer the latest.
 *
 * The publishing actions take publishActionSchema unchanged: they set the same
 * two gates on a different row.
 */
export const releaseNoteRevisionCreateSchema = z.object({
    title: releaseNoteTitleSchema,
    content: releaseNoteContentSchema,
    changeNote: changeNoteSchema,
    publish: z.boolean().default(false),
    baseRevisionNumber: z.number().int().min(0).nullish(),
});
// ---------------------------------------------------------------------------
// Site settings
// ---------------------------------------------------------------------------
/** kb.SiteSettings' titles: nvarchar(200) and never blank (their CHECKs). */
const settingsTitleSchema = z
    .string()
    .trim()
    .min(1, 'A title is required.')
    .max(SITE_TITLE_MAX_LENGTH, `At most ${SITE_TITLE_MAX_LENGTH} characters.`);
/** Its subtitles: nvarchar(500) and allowed to be empty. */
const settingsSubtitleSchema = z
    .string()
    .trim()
    .max(SITE_SUBTITLE_MAX_LENGTH, `At most ${SITE_SUBTITLE_MAX_LENGTH} characters.`)
    .default('');
/**
 * The site's name, gated on the rowversion the Settings page loaded. The icon
 * is not here: it is bytes, not JSON, and travels through its own endpoint
 * exactly as an article image does.
 *
 * The title ALONE since 2026-10-15: the subtitle was always the articles page's
 * line, and moved with that page's own title to articlesSettingsUpdateSchema.
 * STRICT, so a `subtitle` sent here is refused (422) rather than dropped: a
 * Settings page left open across the deploy still sends one, and silently
 * answering "Saved." to an edit that was thrown away would be the worse lie
 * (the review of 2026-10-15). The new page sends none.
 */
export const siteSettingsUpdateSchema = z
    .object({
    title: settingsTitleSchema,
    rowVersion: rowVersionSchema,
})
    .strict('The subtitle is now saved with the articles page\'s title, on the Settings page\'s Articles card. Reload the page.');
/**
 * A Danish heading (2026-10-29, S49): trimmed, and empty - or null - means none,
 * which is the column's NULL, "use the English". So clearing the field clears
 * it, and the CHECK that refuses a blank one is never what answers.
 */
const danishHeadingSchema = (max) => z
    .string()
    .trim()
    .max(max, `At most ${max} characters.`)
    .nullable()
    .transform((value) => (value === null || value === '' ? null : value));
/**
 * A page's heading and the line under it, saved together from the page's own
 * card on the Settings page and gated on the row's one rowversion - the
 * release-note list's (ReleaseNotesTitle / ReleaseNotesSubtitle) and the
 * articles page's (ArticlesTitle / Subtitle), both 2026-10-15. One shape, under
 * two names, so that either can change without the other.
 *
 * `danish` is the same heading in Danish (2026-10-29, S49), both halves at once.
 * Optional, and absent is not empty: a request without it - a Settings page
 * loaded before the Danish fields existed - leaves the stored Danish as it is,
 * rather than clearing text it never showed.
 */
const pageHeadingUpdateSchema = z.object({
    title: settingsTitleSchema,
    subtitle: settingsSubtitleSchema,
    danish: z
        .object({
        title: danishHeadingSchema(SITE_TITLE_MAX_LENGTH),
        subtitle: danishHeadingSchema(SITE_SUBTITLE_MAX_LENGTH),
    })
        .optional(),
    rowVersion: rowVersionSchema,
});
export const articlesSettingsUpdateSchema = pageHeadingUpdateSchema;
export const releaseNotesSettingsUpdateSchema = pageHeadingUpdateSchema;
/**
 * Applying a theme. Its own request rather than a field of the settings PUT
 * because it is an act, not a form edit: previewed on the page, then applied
 * for everyone with one click, and - like the icon - replaced wholesale, so it
 * takes no rowversion. The enum is the shared tuple, which is the CHECK
 * constraint's list.
 */
export const siteThemeUpdateSchema = z.object({
    theme: z.enum(SITE_THEMES),
});
// ---------------------------------------------------------------------------
// Deletion
// ---------------------------------------------------------------------------
/**
 * `satisfies` ties this list to `DeleteTargetKind`: naming a kind the impact
 * machinery does not model fails the build here rather than at runtime. The
 * reverse check below catches the opposite slip - a kind added to the union and
 * forgotten here, which used to compile and then answer 422 to every delete of
 * that kind. 'release-note' joined on 2026-10-12 (S42).
 */
const DELETE_TARGET_KINDS = [
    'article',
    'product',
    'category',
    'release-note',
];
export const deleteTargetKindSchema = z.enum(DELETE_TARGET_KINDS);
export const deleteImpactRequestSchema = z.object({
    kind: deleteTargetKindSchema,
    targetId: z.string().trim().min(1).max(200),
    choices: z.record(z.string().max(100), z.string().max(200).nullable()).optional(),
});
export const deleteConfirmationSchema = z.object({
    confirmationToken: z.string().trim().min(1).max(2000),
    choices: z.record(z.string().max(100), z.string().max(200).nullable()).optional(),
});
// ---------------------------------------------------------------------------
// Images
// ---------------------------------------------------------------------------
/**
 * A flag that may arrive as a real boolean (JSON body) or as a string (a query
 * string parameter), because an image upload puts its fields in the query - the
 * body is the image itself - and a delete carries its flag there too.
 *
 * An unrecognised value is a validation error rather than a silent false. The
 * browser is not trusted here, and a typo that quietly means "no" on a `replace`
 * flag is how an author loses an upload they thought they had confirmed.
 *
 * ONE SPELLING OF TRUE FOR THE WHOLE API. The extract API's `includeDrafts`
 * uses this too, so `true`, `1`, `yes` and `on` mean the same thing wherever a
 * flag is accepted and a caller never has to find out which surface takes
 * which. The message is per-surface because the reader is: a browser form shows
 * it beside a checkbox, while a machine caller has nothing to read but the 422,
 * which is why the extract flag passes one that names the parameter.
 */
const FLAG_TRUE = new Set(['true', '1', 'yes', 'on']);
const FLAG_FALSE = new Set(['false', '0', 'no', 'off', '']);
const flagSchema = (message = 'Expected true or false.') => z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((value, ctx) => {
    if (value === undefined)
        return false;
    if (typeof value === 'boolean')
        return value;
    const normalised = value.trim().toLowerCase();
    if (FLAG_TRUE.has(normalised))
        return true;
    if (FLAG_FALSE.has(normalised))
        return false;
    ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    return z.NEVER;
});
/**
 * A file name that must already be legal: the mirror of CK_ArticleImage_FileName,
 * for the routes that IDENTIFY an image by name - the public GET, the admin
 * preview GET and the DELETE.
 *
 * Strict on purpose in those positions. A name the constraint could never have
 * accepted cannot name a stored row, so the only things it can be are a typo or
 * a probe, and answering it with a validation error costs no database round trip.
 * Note that this does not weaken the 404-for-everything rule on the public
 * endpoint: a name that is merely well-formed and absent still gets the same 404
 * as an unpublished article's image.
 */
export const articleImageFileNameSchema = z
    .string()
    .trim()
    .min(1, 'A file name is required.')
    .max(ARTICLE_IMAGE_FILE_NAME_MAX_LENGTH, `At most ${ARTICLE_IMAGE_FILE_NAME_MAX_LENGTH} characters.`)
    .regex(ARTICLE_IMAGE_FILE_NAME_PATTERN, describeArticleImageFileNameRequirement());
/**
 * The FIELDS of an upload, which arrive in the QUERY STRING. The bytes are not
 * here and cannot be: they are the request BODY, read by `express.raw`, capped at
 * ARTICLE_IMAGE_MAX_BYTES by the parser before anything is buffered, and
 * identified by `inspectArticleImageBytes` from their magic bytes. Nothing about
 * the payload is taken from these fields - not its type, not its length.
 *
 * Because the body is the image, the fields have nowhere to go but the query, so
 * they arrive as text - which is why `replace` is a string flag rather than a
 * boolean. This endpoint does not take a multipart upload and rejects one by name.
 *
 * `fileName` is deliberately LAX here, unlike the strict schema above. A pasted
 * screenshot has no name at all, and a chosen file arrives called
 * "Skaermbillede 2026-08-25 (1).PNG" - so the endpoint normalises whatever it
 * gets through `toArticleImageFileName` and falls back to a generated
 * `pasted-<n>` name. Rejecting the request instead would mean an author cannot
 * upload a file they can see on their own desktop, which is not a rule worth
 * having. The cap is only here to stop an absurd field.
 */
export const articleImageUploadSchema = z.object({
    fileName: z
        .string()
        .trim()
        .max(300, 'At most 300 characters.')
        .optional()
        .transform((value) => (value && value.length > 0 ? value : undefined)),
    /**
     * Overwrite an existing attachment of the same name. Without it a collision is
     * refused, because replacing an image silently changes every article revision
     * that shows it - including the ones already published.
     */
    replace: flagSchema(),
});
/**
 * Deleting one attached image.
 *
 * Not the two-call HMAC impact protocol, and that is a deliberate difference.
 * That protocol exists for deletes whose blast radius reaches other rows - an
 * article takes its revisions, a product tag comes off every article that
 * carries it - where the user has to be shown a computed report before agreeing
 * to it. An image affects exactly one row and one article's rendering, and the
 * whole consequence is knowable from the article's own text: either the
 * markdown still references it or it does not.
 *
 * So the warning is a single flag rather than a signed token. The endpoint
 * refuses while the article's latest revision still references the file and this
 * is false; the editor shows where the references are and asks; the author
 * decides. Cancelling is still the default, which is the property that matters.
 */
export const articleImageDeleteSchema = z.object({
    acknowledgeReferences: flagSchema(),
});
/**
 * An attachment's name (S47), normalised and then judged by the shared rules -
 * the mirror of CK_Attachment_FileName plus the extension allow-list. Unlike an
 * image name it keeps spaces and capitals: it is what the reader downloads, not
 * a markdown target.
 */
export const attachmentFileNameSchema = z
    .string({ required_error: 'A file name is required.' })
    .transform(normaliseAttachmentFileName)
    .superRefine((value, context) => {
    const problem = attachmentFileNameProblem(value);
    if (problem)
        context.addIssue({ code: z.ZodIssueCode.custom, message: problem });
});
/**
 * The fields of an attachment upload - the admin library, the editor and the
 * ingest API alike. The body is the file, so the name is all there is, and it
 * travels in the query string (articleImageUploadSchema's convention, for its
 * reasons).
 */
export const attachmentUploadSchema = z.object({ fileName: attachmentFileNameSchema });
/** An attachment id in a path: a GUID, lower-cased. */
export const attachmentIdSchema = z
    .string()
    .trim()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'Not an attachment id.')
    .transform((value) => value.toLowerCase());
/**
 * Deleting a library attachment (S47). The flag form of the image delete, for
 * the same reason: the consequence is knowable without a signed report - the
 * articles that list or link it - and the endpoint refuses while any do unless
 * this says the administrator has seen them.
 */
export const attachmentDeleteSchema = z.object({
    acknowledgeUsage: flagSchema(),
});
/**
 * Deleting SEVERAL attached images in one request - the editor's "Delete unused"
 * button, which exists so an article that has accumulated a dozen orphaned
 * screenshots is not a dozen dialogs.
 *
 * A JSON body rather than the query string the single delete uses, because the
 * payload is a list: an article can carry dozens of attachments, and dozens of
 * names in a URL is a request some proxy truncates. The flag travels in the same
 * body, in the same spellings as everywhere else.
 *
 * Each name goes through the grammar the single delete applies to its path
 * segment - a name the CHECK constraint could never have stored names no row -
 * and the list is de-duplicated so a client that sends one name twice is not
 * told it deleted two. The cap is generous on purpose: the point of this schema
 * is to take a whole panel's worth at once.
 */
export const ARTICLE_IMAGE_BULK_DELETE_MAX = 200;
export const articleImageBulkDeleteSchema = z.object({
    fileNames: z
        .array(articleImageFileNameSchema)
        .min(1, 'Name at least one image.')
        .max(ARTICLE_IMAGE_BULK_DELETE_MAX, `At most ${ARTICLE_IMAGE_BULK_DELETE_MAX} images in one request.`)
        .transform((names) => [...new Set(names)]),
    acknowledgeReferences: flagSchema(),
});
// ---------------------------------------------------------------------------
// Query strings
// ---------------------------------------------------------------------------
/**
 * The largest id a list may name: the ids are INT IDENTITY columns
 * (kb.Product, kb.Category), so anything larger can never match a row.
 */
const SQL_INT_MAX = 2_147_483_647;
/**
 * Comma-separated integers from a query string: "?products=1,4,9".
 *
 * Junk is dropped, never refused - and that includes an id past INT's range,
 * which used to reach SQL Server and overflow CONVERT(int, ...) inside the
 * OPENJSON filter, turning an anonymous ?products=3000000000 into a 500 on
 * the public lists (found by the S42 review, 2026-10-13).
 */
const csvIdsSchema = z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((value) => {
    const raw = Array.isArray(value) ? value.join(',') : (value ?? '');
    const ids = raw
        .split(',')
        .map((part) => Number.parseInt(part.trim(), 10))
        .filter((id) => Number.isInteger(id) && id > 0 && id <= SQL_INT_MAX);
    return [...new Set(ids)].slice(0, 200);
});
const pageSchema = z.coerce.number().int().min(1).max(10_000).default(1);
const pageSizeSchema = z.coerce.number().int().min(1).max(100).default(20);
/**
 * The longest search any list accepts, in characters. Exported because the
 * server sizes its LIKE pattern parameter from it: toLikePattern can double
 * every character, so the pattern needs 2 x this + 2 (S43 review, 2026-10-13).
 */
export const SEARCH_MAX_LENGTH = 200;
const searchSchema = z
    .string()
    .trim()
    .max(SEARCH_MAX_LENGTH)
    .optional()
    .transform((value) => (value && value.length > 0 ? value : null));
// ---------------------------------------------------------------------------
// Ingest API - machine-created draft articles
// ---------------------------------------------------------------------------
/** A taxonomy slug as the ingest caller names it: same grammar the tables CHECK. */
const TAXONOMY_SLUG_PATTERN = /^[a-z0-9-]{1,200}$/;
const TAXONOMY_SLUG_MESSAGE = 'Slugs are lowercase letters, digits and hyphens.';
const taxonomySlugSchema = z.string().regex(TAXONOMY_SLUG_PATTERN, TAXONOMY_SLUG_MESSAGE);
/**
 * The same grammar, said as one required field rather than a list. Its own
 * schema only so that an omitted product reads as "A product slug is required."
 * instead of zod's bare "Required" - the caller is a script or an LLM working
 * from the template in docs/ARTICLE-INGEST-API.md, and the 422 is the only
 * documentation it will read.
 */
const ingestProductSlugSchema = z
    .string({
    required_error: 'A product slug is required.',
    invalid_type_error: 'A product slug is required.',
})
    .regex(TAXONOMY_SLUG_PATTERN, TAXONOMY_SLUG_MESSAGE);
/**
 * One inline image, base64 inside the JSON body: a webhook caller builds one
 * self-contained request, and base64 survives every JSON serializer. The
 * length cap is the 5 MB byte cap after base64's 4/3 expansion - a cheap
 * refusal before anyone decodes anything. The server sniffs the real content
 * type from the bytes; there is deliberately no contentType field to lie in.
 */
const ingestImageSchema = z.object({
    fileName: articleImageFileNameSchema,
    base64: z
        .string()
        .min(1)
        .max(Math.ceil(ARTICLE_IMAGE_MAX_BYTES / 3) * 4 + 8, 'The encoded image exceeds the 5 MB limit.')
        .regex(/^[A-Za-z0-9+/\r\n]+={0,2}$/, 'images[].base64 must be standard base64.'),
});
/**
 * The article's own ceiling - ARTICLE_IMAGE_MAX_PER_ARTICLE, twenty - and not a
 * number of this API's own. It used to be a generous 50, with the 40 MB body as
 * the limit that actually bound; since 2026-09-21 an article may carry at most
 * twenty images (see articleImages.ts for why), so a payload naming more can
 * never succeed and is refused by the schema before a byte is decoded. On an
 * UPDATE the count that matters is what the article would carry AFTERWARDS -
 * what is attached plus every name that is new - and the route checks that
 * against the stored list before it writes anything, so a request that would
 * overrun is turned away whole rather than half-applied.
 */
export const INGEST_MAX_IMAGES = ARTICLE_IMAGE_MAX_PER_ARTICLE;
/**
 * Two images of one name in a single payload is a caller bug, not a request to
 * overwrite: the second would replace the first before a human ever saw either.
 * Shared by both ingest operations so the rule cannot drift apart between them.
 */
const hasUniqueFileNames = (images) => new Set(images.map((image) => image.fileName)).size === images.length;
const INGEST_IMAGE_NAMES_MESSAGE = 'Image file names must be unique within one article.';
/**
 * Who or what generated a payload, as the caller declares it: printable ASCII,
 * 1-200. The value is caller-asserted and lands next to real principals in the
 * revision history and the audit trail, so control characters and bidi tricks
 * that could dress it up as one are refused outright.
 *
 * Kept as a pattern and a message rather than one shared schema because the two
 * operations differ on exactly one point - create's author is optional, an
 * update's is required - and `required_error` has to be given where the schema
 * is built. The grammar is the part that must not differ, so the grammar is what
 * is shared.
 */
const INGEST_AUTHOR_PATTERN = /^[ -~]{1,200}$/;
const INGEST_AUTHOR_MESSAGE = 'author must be 1-200 printable ASCII characters.';
/**
 * Who may read the article (2026-10-26, S46), under the editor's two field
 * names. Only the grammar is checked here. Whether a key is one this deployment
 * CONFIGURES is the route's question, answered as an unknown category slug is -
 * a 422 that lists the valid keys; what the two fields mean together is
 * resolveIngestReaders', because on an update that depends on what the article
 * already has.
 */
const INGEST_VISIBILITY_MESSAGE = 'visibility must be "public" or "private".';
const ingestVisibilitySchema = z.enum(ARTICLE_VISIBILITIES, {
    errorMap: () => ({ message: INGEST_VISIBILITY_MESSAGE }),
});
/**
 * The whole article in one payload. Taxonomy is referenced by SLUG, not id -
 * slugs are stable and human-readable, which is what lets an LLM be handed a
 * template instead of a database dump. There is no isVisible field on
 * purpose: everything this API creates is born hidden and stays so until a
 * human publishes it from the admin UI.
 *
 * `productSlug` is singular and required, matching kb.Article.ProductId. It is
 * also the first segment of the address the created article will answer on, so
 * an unknown one cannot be shrugged off: the route answers 422 listing the
 * product slugs that do exist, which is what lets a caller correct itself
 * without a second document to consult.
 */
export const ingestArticleSchema = z.object({
    title: titleSchema,
    /**
     * Markdown. Same body the editor saves; image references included, and
     * [[kb:<product>/<slug>]] links - a bare slug in a token is half an address
     * and will not resolve. See docs/ARTICLE-LINK-TOKENS.md.
     */
    content: contentSchema.min(1, 'The article body is required.'),
    productSlug: ingestProductSlugSchema,
    categorySlugs: z.array(taxonomySlugSchema).min(1, 'Assign at least one category tag.').max(20),
    /** Optional; derived from the title when absent, suffixed when taken WITHIN the product. */
    slug: articleSlugSchema.optional(),
    /**
     * Who may read the article once a human publishes it (S46). Absent is public,
     * as every article was before private articles existed; "private" needs at
     * least one key in audienceKeys.
     */
    visibility: ingestVisibilitySchema.optional(),
    /** Audience KEYS, not names - as categories go by slug. Only with "private". */
    audienceKeys: audienceKeysSchema.optional(),
    changeNote: changeNoteSchema,
    /**
     * Who or what generated this - recorded as ingest:<author> in the audit
     * trail. Optional on a create and required on an update; the grammar both
     * spell is INGEST_AUTHOR_PATTERN above, which says why it is this narrow.
     */
    author: z
        .string()
        .trim()
        .regex(INGEST_AUTHOR_PATTERN, INGEST_AUTHOR_MESSAGE)
        .optional(),
    images: z
        .array(ingestImageSchema)
        .max(INGEST_MAX_IMAGES, `At most ${INGEST_MAX_IMAGES} images per article.`)
        .default([])
        .refine(hasUniqueFileNames, { message: INGEST_IMAGE_NAMES_MESSAGE }),
});
// ---------------------------------------------------------------------------
// Ingest API - updating an article that already exists
// ---------------------------------------------------------------------------
/**
 * Blank means ABSENT on an update, and it has to mean that BEFORE the field's
 * own grammar runs.
 *
 * `title: ""` is a caller saying "leave the title alone", never "set the title
 * to nothing". That is the rule for every optional field of an update, and it
 * is what makes the extract API's output a legal update payload: an article
 * with no revision extracts as `content: ""` and an untagged one as
 * `categorySlugs: []`, and echoing either back must not blank the article or
 * fail the request. Null and a missing key say the same thing. There is
 * deliberately no way to CLEAR a field through this API - a machine that empties
 * a human's title by omission is the failure this endpoint most needs not to
 * have, and the admin UI is where a field gets emptied on purpose.
 *
 * Hence z.preprocess rather than a `.transform()` chained onto the end: a
 * transform only ever sees what the grammar already accepted, and `""` would
 * never get that far - titleSchema's minimum would have refused it first. The
 * non-blank value is passed through UNTOUCHED, so each field still does its own
 * trimming and keeps its own message.
 *
 * Arrays get the rule one level down: blank entries are dropped first, and an
 * array with nothing left in it is absent too. So `categorySlugs: []` does not
 * clear an article's tags - there is no way to say that here, on purpose, since
 * an article with no category is an authoring error the admin UI refuses to save
 * and a machine should not be able to produce one by sending an empty list.
 * `images: []` is the same statement and gets the same answer.
 */
const absentWhenBlank = (value) => {
    if (value === null || value === undefined)
        return undefined;
    if (typeof value === 'string')
        return value.trim().length > 0 ? value : undefined;
    if (Array.isArray(value)) {
        const entries = value.filter((entry) => typeof entry !== 'string' || entry.trim().length > 0);
        return entries.length > 0 ? entries : undefined;
    }
    // A number where a string belongs, an object where an array does: handed to
    // the field's own grammar, which refuses it by name. Swallowing it as "absent"
    // would turn a caller's type error into a silent no-op - the one outcome an
    // automated caller cannot detect.
    return value;
};
/**
 * The rule above applied to one field. Written once and reused by all eight,
 * because eight near-identical preprocess chains is how one of them ends up
 * subtly different from the other seven.
 *
 * READING THE RESULT: an absent field is `undefined`, but its KEY may still be
 * present - zod keeps a key the input had, whatever the preprocess made of its
 * value, so `title: ""` parses to an object where `'title' in input` is true and
 * `input.title` is undefined. Consumers must test `input.title !== undefined`.
 * Deleting the keys instead would mean a `.transform()` over the whole object,
 * which turns this schema into a ZodEffects and costs `.shape` and `.extend()`
 * for a convenience nothing here needs.
 */
function optionalWhenBlank(schema) {
    return z.preprocess(absentWhenBlank, schema.optional());
}
const INGEST_ARTICLE_ID_MESSAGE = 'articleId must be the uuid the create or extract API returned for this article.';
const INGEST_CHANGE_NOTE_MESSAGE = 'A change note is required: say what changed and why.';
/**
 * An update's change note: required, 1-500. Shared by the article and the
 * release-note update, whose reasons for requiring it are the same - see the
 * field's comment in ingestArticleUpdateSchema.
 */
const ingestRequiredChangeNoteSchema = z
    .string({
    required_error: INGEST_CHANGE_NOTE_MESSAGE,
    invalid_type_error: INGEST_CHANGE_NOTE_MESSAGE,
})
    .trim()
    .min(1, INGEST_CHANGE_NOTE_MESSAGE)
    .max(500, 'At most 500 characters.');
/** An update's author: required, in the INGEST_AUTHOR_PATTERN grammar. Shared likewise. */
const ingestRequiredAuthorSchema = z
    .string({
    required_error: INGEST_AUTHOR_MESSAGE,
    invalid_type_error: INGEST_AUTHOR_MESSAGE,
})
    .trim()
    .regex(INGEST_AUTHOR_PATTERN, INGEST_AUTHOR_MESSAGE);
/**
 * The ingest API's attachment upload (2026-10-28, S47): PUT
 * /api/ingest/articles/{articleId}/attachments?fileName=...&author=..., the
 * file as the body. Part of the UPDATE operation - it changes an article that
 * exists - so the author is required, as an update's is, and travels in the
 * query string beside the name because the body is the file.
 */
export const ingestAttachmentUploadSchema = attachmentUploadSchema.extend({ author: ingestRequiredAuthorSchema });
/** PUT /api/ingest/articles/{articleId}/attachments/{attachmentId}?author=... - a library file put on the list. */
export const ingestAttachmentListSchema = z.object({ author: ingestRequiredAuthorSchema });
/**
 * Updating an article that already exists: the body of PUT /api/ingest/articles,
 * naming its target by the id the create and extract APIs hand out.
 *
 * Its own schema rather than `ingestArticleSchema.partial()`, and the difference
 * is not cosmetic: the two operations disagree about what is required in BOTH
 * directions. An update needs an articleId and a changeNote that a create does
 * not, and lets go of the title, content, product and categories a create
 * insists on - so `.partial()` would have produced something that accepts a
 * payload containing nothing but an id, with nowhere to say "the note is
 * required here and optional there".
 *
 * No isVisible field, for the reason the create schema gives: nothing this API
 * touches can publish itself. The update goes further, and a caller has to know
 * it because the schema cannot express it - a supplied title or content lands as
 * a DRAFT revision that a human publishes from /admin/articles, while a supplied
 * slug, product, category set, visibility or audience list takes effect the
 * moment the request succeeds.
 * docs/ARTICLE-INGEST-API.md states that asymmetry plainly; it is the thing a
 * caller would otherwise discover by surprise.
 */
export const ingestArticleUpdateSchema = z.object({
    /**
     * Which article. Matched against kb.Article.ArticleId, and a miss is a 404
     * rather than an implicit create: articles are created by the create
     * operation, which is the whole of "only updates will be allowed here".
     */
    articleId: z
        .string({
        required_error: INGEST_ARTICLE_ID_MESSAGE,
        invalid_type_error: INGEST_ARTICLE_ID_MESSAGE,
    })
        .trim()
        .uuid(INGEST_ARTICLE_ID_MESSAGE),
    /**
     * Required, where the create operation's is optional and nullable. A machine
     * editing an article a human wrote says why: this note is all the revision
     * history and the audit trail will show whoever finds the edit later, and an
     * empty one tells them nothing. 1-500, the ChangeNote column's width.
     */
    changeNote: ingestRequiredChangeNoteSchema,
    /**
     * Required here too, and for the same reason: an edit to somebody else's
     * article is signed. One message for every way it can be wrong - missing,
     * null, a number, a control character - because they all mean the same thing
     * to the script that has to fix it.
     */
    author: ingestRequiredAuthorSchema,
    /** Absent leaves the title alone. Supplied, it arrives as a draft revision. */
    title: optionalWhenBlank(titleSchema),
    /**
     * The second segment of the article's address. Supplying it MOVES the article,
     * so the route refuses the move when the resulting <product>/<slug> pair is
     * another article's, and rewrites every [[kb:...]] token naming the old
     * address when it is not. Refused rather than suffixed as a create is: an
     * update must not silently park an article at an address nobody asked for.
     */
    slug: optionalWhenBlank(articleSlugSchema),
    /** The first segment of the address. Same grammar as a create's, same consequences as `slug`. */
    productSlug: optionalWhenBlank(taxonomySlugSchema),
    /**
     * Replaces the category set wholesale when supplied - this is not a patch
     * grammar and there is no "add one tag". No minimum of one, unlike the create
     * schema, because absent already means "leave them alone": a list that arrives
     * empty, or empty after its blank entries are dropped, is absent rather than a
     * request the schema has to refuse. See absentWhenBlank.
     */
    categorySlugs: optionalWhenBlank(z.array(taxonomySlugSchema).max(20, 'At most 20 category tags.')),
    /**
     * Who may read the article (S46). Takes effect AT ONCE, like the address: it
     * belongs to the article, not to a revision, so a published article made
     * public is read by everybody the moment the request succeeds. Absent leaves
     * it alone.
     */
    visibility: optionalWhenBlank(ingestVisibilitySchema),
    /**
     * Replaces a private article's audiences wholesale, as categorySlugs replaces
     * its tags. Absent - or empty - leaves them alone, so the only way to clear
     * them is to make the article public. Refused for an article that is, or is
     * being made, public: see resolveIngestReaders.
     */
    audienceKeys: optionalWhenBlank(audienceKeysSchema),
    /**
     * Markdown, up to the same cap the editor saves against, and with no minimum:
     * blank is absent, so there is nothing left for a minimum to refuse. Same
     * [[kb:<product>/<slug>]] link grammar - see docs/ARTICLE-LINK-TOKENS.md.
     */
    content: optionalWhenBlank(contentSchema),
    /**
     * Upserted by file name, never deleted: a name that already exists replaces
     * the stored bytes, a new name is added, and anything attached but not
     * mentioned is left exactly as it is. An update carrying no images is
     * therefore an article that keeps its screenshots, not one stripped of them.
     */
    images: optionalWhenBlank(z
        .array(ingestImageSchema)
        .max(INGEST_MAX_IMAGES, `At most ${INGEST_MAX_IMAGES} images per article.`)
        .refine(hasUniqueFileNames, { message: INGEST_IMAGE_NAMES_MESSAGE })),
});
// ---------------------------------------------------------------------------
// Ingest API - release notes (S42, 2026-10-12)
// ---------------------------------------------------------------------------
/**
 * The release note's own image ceiling, RELEASE_NOTE_IMAGE_MAX_PER_NOTE, for the
 * reason INGEST_MAX_IMAGES is the article's: a payload the note can never hold
 * is refused before a byte is decoded.
 */
const INGEST_RELEASE_NOTE_IMAGES_MESSAGE = `At most ${RELEASE_NOTE_IMAGE_MAX_PER_NOTE} images per release note.`;
const INGEST_RELEASE_NOTE_IMAGE_NAMES_MESSAGE = 'Image file names must be unique within one release note.';
/**
 * The whole release note in one payload: POST /api/ingest/release-notes. The
 * article create's contract with the release note's fields - a title held to
 * the release-note rules, a required body, the product by SLUG (an unknown one
 * is a 422 that lists the slugs that exist), and the version, which together
 * with the product is the note's address. A version that product already has
 * is a 409, never a suffix: unlike a slug, a version is a fact about the
 * release, not a name the API may adjust.
 *
 * No isVisible: everything this API creates is born hidden, and a human
 * publishes it from /admin/releasenotes.
 */
export const ingestReleaseNoteSchema = z.object({
    title: releaseNoteTitleSchema,
    /** Markdown, with the article-link tokens and image references an article body takes. */
    content: releaseNoteContentSchema.min(1, 'The release note body is required.'),
    productSlug: ingestProductSlugSchema,
    version: releaseNoteVersionSchema,
    changeNote: changeNoteSchema,
    /** Recorded as ingest:<author>. Optional on a create, as for articles. */
    author: z
        .string()
        .trim()
        .regex(INGEST_AUTHOR_PATTERN, INGEST_AUTHOR_MESSAGE)
        .optional(),
    images: z
        .array(ingestImageSchema)
        .max(RELEASE_NOTE_IMAGE_MAX_PER_NOTE, INGEST_RELEASE_NOTE_IMAGES_MESSAGE)
        .default([])
        .refine(hasUniqueFileNames, { message: INGEST_RELEASE_NOTE_IMAGE_NAMES_MESSAGE }),
});
const INGEST_RELEASE_NOTE_ID_MESSAGE = 'releaseNoteId must be the uuid the create or extract API returned for this release note.';
/**
 * Updating a release note that already exists: PUT /api/ingest/release-notes,
 * naming its target by id. ingestArticleUpdateSchema's rules, field for field,
 * and for its reasons: the id, the change note and the author are required;
 * everything else is optional, and blank means ABSENT (absentWhenBlank), so an
 * extracted note echoed back changes nothing it did not mean to.
 *
 * The same asymmetry a caller has to know, too: a supplied title or content
 * lands as a DRAFT revision a human publishes, while a supplied productSlug or
 * version MOVES the note the moment the request succeeds - refused with a 409
 * when that product already has the version.
 */
export const ingestReleaseNoteUpdateSchema = z.object({
    releaseNoteId: z
        .string({
        required_error: INGEST_RELEASE_NOTE_ID_MESSAGE,
        invalid_type_error: INGEST_RELEASE_NOTE_ID_MESSAGE,
    })
        .trim()
        .uuid(INGEST_RELEASE_NOTE_ID_MESSAGE),
    changeNote: ingestRequiredChangeNoteSchema,
    author: ingestRequiredAuthorSchema,
    title: optionalWhenBlank(releaseNoteTitleSchema),
    content: optionalWhenBlank(releaseNoteContentSchema),
    productSlug: optionalWhenBlank(taxonomySlugSchema),
    version: optionalWhenBlank(releaseNoteVersionSchema),
    images: optionalWhenBlank(z
        .array(ingestImageSchema)
        .max(RELEASE_NOTE_IMAGE_MAX_PER_NOTE, INGEST_RELEASE_NOTE_IMAGES_MESSAGE)
        .refine(hasUniqueFileNames, { message: INGEST_RELEASE_NOTE_IMAGE_NAMES_MESSAGE })),
});
// ---------------------------------------------------------------------------
// Extract API - the knowledge base read back out, ingest-shaped
// ---------------------------------------------------------------------------
/**
 * How many articles one page carries when the caller does not say, and the
 * most it will carry if they do.
 *
 * The cap is what keeps a response bounded, and the number that matters is not
 * the article count but the image bytes hanging off it: measured on the TEST
 * corpus, 474 images totalling 45 MB across ~100 articles - about 390 KB of
 * base64 per article on average, with a 5 MB ceiling per image. Ten articles is
 * therefore a few megabytes, and fifty is the point past which a single
 * response stops being something a caller can retry cheaply.
 */
export const EXTRACT_DEFAULT_PAGE_SIZE = 10;
export const EXTRACT_MAX_PAGE_SIZE = 50;
/**
 * The extract query string.
 *
 * `page` is ONE-indexed: page 1 is the first page, and the last page is
 * `totalPages`, so the two numbers a caller sees side by side in the response
 * read against each other without a mental subtraction - `page: 4` of
 * `totalPages: 4` is the last page, not one past it. It is also how every other
 * paged surface in this codebase counts (pageSchema above). The endpoint shipped
 * zero-indexed on 2026-09-15, reached TEST with its token already in place, and
 * was changed to this the next day - so an automation that had already started
 * paging it from 0 may exist, and refusing page 0 below is what tells it so
 * instead of quietly shifting its first page. The loop is
 * `for (page = 1; page <= totalPages; page++)`.
 *
 * Not `pageSchema` itself, because this surface's caller is a script and the
 * 422 is the only documentation it reads: the messages below say exactly what
 * a page number may be, where the generic schema's would say "Number must be
 * greater than or equal to 1".
 *
 * Coerced from strings because a query string has no numbers, and refused
 * rather than clamped when out of range - a caller asking for 500 articles has
 * misunderstood something, and a truncated answer that looks like a full one is
 * the worse failure. Page 0 is refused for the same reason: under the old
 * numbering it was the first page, and quietly serving page 1 for it would hide
 * exactly the caller that has not read the contract.
 *
 * GET /api/extract/release-notes takes this same query (S42, 2026-10-12): the
 * same paging, the same page-size cap and the same `includeDrafts`, so a caller
 * that pages one extract pages the other.
 */
export const extractQuerySchema = z.object({
    page: z.coerce
        .number({ invalid_type_error: 'page must be a whole number, 1 or greater.' })
        .int('page must be a whole number, 1 or greater.')
        .min(1, 'page must be a whole number, 1 or greater.')
        .max(1_000_000)
        .default(1),
    pageSize: z.coerce
        .number({ invalid_type_error: `pageSize must be a whole number between 1 and ${EXTRACT_MAX_PAGE_SIZE}.` })
        .int(`pageSize must be a whole number between 1 and ${EXTRACT_MAX_PAGE_SIZE}.`)
        .min(1, `pageSize must be a whole number between 1 and ${EXTRACT_MAX_PAGE_SIZE}.`)
        .max(EXTRACT_MAX_PAGE_SIZE, `pageSize must be a whole number between 1 and ${EXTRACT_MAX_PAGE_SIZE}.`)
        .default(EXTRACT_DEFAULT_PAGE_SIZE),
    /**
     * Hand back the NEWEST text rather than the REVIEWED text.
     *
     * Off by default, and the default is the safe one: without this flag the
     * extract carries the current revision, which is what readers get and what a
     * human approved. With it, an article whose author has a draft ahead of the
     * published version comes back as that draft, and an article that has only
     * ever been a draft - never published, or published and then withdrawn -
     * appears at all, which it otherwise does not. Both change what a page
     * CONTAINS, and the second changes how many pages there are, so `totalPages`
     * is not comparable across the two modes.
     *
     * It is a separate flag rather than the new behaviour because the two answer
     * different questions. "What does this knowledge base say?" is the published
     * text; "what is being written in it?" is this. A mirror that fed drafts into
     * another instance unasked would be republishing sentences nobody has read.
     *
     * Every article in the response then carries `isDraft`, which says which of
     * the two it is - present only in this mode, so a caller that did not ask for
     * drafts cannot be handed one without noticing the shape change.
     */
    includeDrafts: flagSchema('includeDrafts must be true or false (1 and 0 also work).'),
});
// ---------------------------------------------------------------------------
// Promoted articles - the shortlist behind the public tiles
// ---------------------------------------------------------------------------
/**
 * The public site renders at most this many tiles, so the admin surface
 * refuses to store more: a sixth promotion that can never be shown is a lie
 * waiting to be discovered. One number, shared by the contract, the admin UI
 * and the tile row.
 */
export const MAX_PROMOTED_ARTICLES = 5;
/**
 * The whole shortlist in one payload, replace-all semantics: array order IS
 * the display order, which keeps reordering trivially atomic on the server
 * (one transaction, one unique-per-position constraint) and spares the wire a
 * patch grammar for a list of five.
 */
export const promotedArticlesInputSchema = z.object({
    articleIds: z
        .array(z.string().uuid())
        .max(MAX_PROMOTED_ARTICLES)
        .refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length, { message: 'An article can only be promoted once.' }),
});
// ---------------------------------------------------------------------------
// Frequent questions - the front page's list of links (S51, 2026-11-04)
// ---------------------------------------------------------------------------
/**
 * The owner's "up to 8". Not only the contract's: kb.FrequentQuestion is keyed
 * by position and CK_FrequentQuestion_SortOrder allows 1 to 8, so the database
 * cannot hold a ninth either. One number, shared by this contract, the
 * Questions page and the front page's list.
 */
export const MAX_FREQUENT_QUESTIONS = 8;
/** kb.FrequentQuestion.Question is NVARCHAR(200). */
export const FREQUENT_QUESTION_MAX_LENGTH = 200;
/**
 * One question: the sentence a reader clicks, and the article it leads to.
 *
 * The sentence is trimmed first - CK_FrequentQuestion_Question refuses one
 * stored with a leading or trailing space - then held to 1 to 200 characters
 * and one line, the release-note title's rule (isSingleLineTitle): it is link
 * text in a list, and a line break inside it is a mistake, not a layout. The
 * database leaves "one line" to this contract; its header says why.
 */
export const frequentQuestionSchema = z.object({
    question: z
        .string()
        .trim()
        .min(1, 'A question is required.')
        .max(FREQUENT_QUESTION_MAX_LENGTH, `At most ${FREQUENT_QUESTION_MAX_LENGTH} characters.`)
        .refine(isSingleLineTitle, 'A question is a single line.'),
    articleId: z.string().uuid(),
});
/**
 * The whole list in one payload, the promoted shortlist's replace-all: array
 * order IS the display order, and saving rewrites the table in one
 * transaction. Two questions may lead to the same article, so - unlike the
 * shortlist - nothing here refuses a repeated article id.
 */
export const frequentQuestionsInputSchema = z.object({
    questions: z
        .array(frequentQuestionSchema)
        .max(MAX_FREQUENT_QUESTIONS, `At most ${MAX_FREQUENT_QUESTIONS} questions.`),
});
export const publicArticleQuerySchema = z.object({
    products: csvIdsSchema,
    categories: csvIdsSchema,
    q: searchSchema,
    page: pageSchema,
    pageSize: pageSizeSchema,
    sort: z.enum(['recent', 'title']).default('recent'),
});
/**
 * Kept in step with `AdminArticleStatusFilter` by the same `satisfies` trick as
 * the delete kinds above: the DTO type and the query parser are two
 * declarations of one list, and this is what stops them drifting. The reverse
 * check below (2026-10-12) makes it both directions, which is what the type's
 * comment in types.ts promises: `satisfies` alone let a member added to the
 * union and forgotten here compile.
 */
const ADMIN_ARTICLE_STATUS_FILTERS = [
    'all',
    'live',
    'scheduled',
    'hidden',
    'draft',
    'withdrawn',
    'unwritten',
    'untagged',
];
export const adminArticleQuerySchema = z.object({
    q: searchSchema,
    products: csvIdsSchema,
    categories: csvIdsSchema,
    status: z.enum(ADMIN_ARTICLE_STATUS_FILTERS).default('all'),
    page: pageSchema,
    pageSize: pageSizeSchema,
    sort: z.enum(['modified', 'created', 'publish', 'title']).default('modified'),
});
/**
 * The saved views of the admin release-note list, in the order the list page
 * offers them: the article list's views without 'untagged', since a release note
 * has no categories. Tied to `AdminReleaseNoteStatusFilter` in both directions -
 * `satisfies` one way, the AssertTrue alias below the other - and exported so the
 * list page builds its options from the same tuple the server parses with.
 */
export const ADMIN_RELEASE_NOTE_STATUS_FILTERS = [
    'all',
    'live',
    'scheduled',
    'hidden',
    'draft',
    'withdrawn',
    'unwritten',
];
/** The release-note list's sort orders, tied to `AdminReleaseNoteSort` both ways. */
const ADMIN_RELEASE_NOTE_SORTS = [
    'modified',
    'created',
    'publish',
    'title',
];
/** GET /api/admin/release-notes: the article list's query without categories. */
export const adminReleaseNoteQuerySchema = z.object({
    q: searchSchema,
    products: csvIdsSchema,
    status: z.enum(ADMIN_RELEASE_NOTE_STATUS_FILTERS).default('all'),
    page: pageSchema,
    pageSize: pageSizeSchema,
    sort: z.enum(ADMIN_RELEASE_NOTE_SORTS).default('modified'),
});
/**
 * Release notes on one public page when the reader does not say, and the most
 * one page may carry. Smaller than the article list's twenty because every item
 * is a WHOLE note - full markdown, images and link targets - not an excerpt
 * (the owner's changelog shows each entry in full).
 */
export const PUBLIC_RELEASE_NOTE_PAGE_SIZE = 10;
export const PUBLIC_RELEASE_NOTE_MAX_PAGE_SIZE = 50;
/**
 * GET /api/public/release-notes: the product filter, as `?products=<ids>` in the
 * browse page's spelling, the reader's search as `?q=`, and paging. No sort: the
 * list is always newest publish date first. Out-of-range numbers are refused, not
 * clamped, as on the article list.
 *
 * `q` since 2026-10-13 (S43, the owner's "release notes search, similar to
 * articles search"): the article list's own searchSchema - trimmed, at most 200
 * characters (a longer one is a 422, not a silent cut), blank meaning no search -
 * so the two searches cannot disagree on what a search is. It only NARROWS the
 * list: the owner's answer the same day was that results keep the changelog's
 * order, so there is no relevance sort to choose, and none is offered.
 */
export const publicReleaseNoteQuerySchema = z.object({
    products: csvIdsSchema,
    q: searchSchema,
    page: pageSchema,
    pageSize: z.coerce
        .number()
        .int()
        .min(1)
        .max(PUBLIC_RELEASE_NOTE_MAX_PAGE_SIZE)
        .default(PUBLIC_RELEASE_NOTE_PAGE_SIZE),
});
/**
 * Flattens a ZodError into the `details` map of ApiErrorBody: dotted path to
 * messages, which is exactly the shape the admin forms index by field name.
 */
export function toFieldErrors(error) {
    const details = {};
    for (const issue of error.issues) {
        const key = issue.path.length > 0 ? issue.path.join('.') : '_';
        const bucket = details[key] ?? [];
        bucket.push(issue.message);
        details[key] = bucket;
    }
    return details;
}
//# sourceMappingURL=contracts.js.map